import { Fragment, memo, useRef, useState } from "react";
import { Button, OverlayTrigger } from "react-bootstrap";
import { Trash, EyeFill, Link45deg, Paperclip, Clipboard2CheckFill } from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { getTextWidth, useIsMobileApp } from "../store/api/CommonFunctions";
/**
 * 
 * @param {{row:Object, onclick:function, cols:Object[], buttons:Object[], onDoubleClick:function, forceRowClickInMobile:boolean, onShowTooltip:function, displayRow:int, setDisplayRow:function}} props
 * @returns 
 */
const TableRow =memo(({ row, onclick, cols, indx, buttons = [], onDoubleClick, forceRowClickInMobile, onShowTooltip, displayRow, setDisplayRow }) => {
    const buttonClicked = useRef(false);
    const clickTimeout = useRef(null);

    const isMobile = useIsMobileApp();
    const isWeb = !isMobile;

    const rowNumber = cols.find(c => c.colName?.toLowerCase() === "rownumber");
    const visibleCols = cols.filter(c => c.colName?.toLowerCase() !== "rownumber");
    const mobileCols = visibleCols.filter(c => c.showInMobile);
    const webCols = visibleCols.filter(c => !c.showInMobile);

    const handleRowClick = (e) => {
        if (e.target.tagName.toLowerCase() !== "td" || buttonClicked.current) {
            buttonClicked.current = false;
            return;
        }
        if (isMobile) setDisplayRow(prev => prev !== indx ? indx : -1);
        if (isWeb || forceRowClickInMobile) onclick?.({ ...row, DataIndex: row.RowIndex, RowIndex: indx, ctrlIsDown: e.ctrlKey, target: e.target });
    };

    const handleColChange = (e, fn) => {
        const colName = e.target.getAttribute("colname");
        row[colName] = e.target.checked;
        fn?.({ e, ...row })
    };
    const handleDoubleClick = (e) => {
        onDoubleClick?.({ ...row, RowIndex: indx });
    }
    const onRowClick = (e) => {
        if (!onDoubleClick) {
            handleRowClick(e);
            return;
        }
        // اول پاک می‌کنیم چون ممکنه وقفه‌ی قبلی مونده باشد
        clearTimeout(clickTimeout.current);

        clickTimeout.current = setTimeout(() => {
            handleRowClick(e);
        }, 100);
    };

    const onRowDoubleClick = (e) => {
        clearTimeout(clickTimeout.current);
        handleDoubleClick(e);
    };

    const getColData = (row, colName, seperator = "-") => {
        return colName.split("+").map(cn => row[cn]?.toString().trim()).filter(Boolean).join(` ${seperator} `);
    };

    const getCellContent = (item) => {
        let value = row[item.colName];
        if (!(value?.toString().length > 0) && (!item.colType || item.colType === "string")) {
            value = "---";
        }
        switch (item.colType) {
            case "currency": return (!value ? 0 : (item.showNegative ? value : Math.abs(value))).toLocaleString(navigator.language);
            case "boolean": return <input type="checkbox" className="tableRowColCheck form-check-input" defaultChecked={value} disabled={!item.editable} onChange={e => handleColChange(e, item.onChange)} colname={item.colName} />;
            case "number": return value;
            case "string": return item.colName.includes("+") ? getColData(row, item.colName, item.seprator) : isWeb ? (value?.substring(0, item.maxTextLength) + (value?.length > item.maxTextLength ? "..." : "")) : value;
            case "color": return <input type="color" readOnly value={value} disabled style={{ padding: 0, border: "none", width: "100%", minWidth: "20px" }} />;
            case "calc": return item.valueFunction?.(row);
            case "img": return <img src={`data:image/png;base64,${value}`} width={100} alt="بدون تصویر" onClick={item.onClick ? () => item.onClick(row) : undefined} />;
            default: return value;
        }
    };

    const renderCell = (item, i) => {
        const content = getCellContent(item);
        const value = row[item.colName];

        return (item.showCondition === undefined || item.showCondition(row)) && (isWeb || (isMobile && item.showInMobile)) && (
            <td
                key={item.colName || i}
                style={item.style ?? item.styleCondition?.(row)}
                className={item.className}
                colname={item.colName}
                onMouseEnter={e => showTdTooltip({ e, ...item })}
                onMouseLeave={handleMouseLeave}
            >
                {content}
                {item.hasTooltip && value && item.maxTextLength < value.length && <button
                    onClick={() => {
                        navigator.clipboard.writeText(value);

                    }}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "#fff",
                        cursor: "pointer",
                        fontSize: "14px",
                        outline: "none"
                    }}
                    title="کپی"
                >
                    <Clipboard2CheckFill color="#ff5700" />
                </button>}
                {item.button && (item.button.showCondition === undefined || item.button.showCondition(row)) && (
                    <button className={(item.button.className ?? "") + " table-row-button"} onClick={() => item.button.onClick?.(row)}>
                        {item.button.icon}{item.button.caption}
                    </button>
                )}
            </td>
        );
    };

    const renderButtons = () => buttons.filter(b => b.showCondition === undefined || b.showCondition(row)).map((b, i) => {
        const handleClick = (e) => { buttonClicked.current = true; b.onclick?.({ ...row, e }); };
        const btnProps = {
            className: "rowBtn" + (isMobile && b.className ? " " + b.className : ""),
            style: { marginRight: "3px", ...(b.style || {}) },
            onClick: handleClick
        };
        if (isMobile) {
            return <Button {...btnProps} key={i}>{b.title}</Button>
        }
        switch (b.type) {
            case "delete": return <Trash {...btnProps} color="red" size={18} key={i} />;
            case "show": return <EyeFill {...btnProps} color="#9377d1" size={18} key={i} />;
            case "attach": return <Paperclip {...btnProps} color="blue" size={18} key={i} />;
            case "related": return <Link45deg {...btnProps} color="blue" size={18} key={i} />;
            case "common": return (<button {...btnProps} className={"btn btn-row-icon p-1"} data-tooltip={b.tooltip} onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}>{b.icon}</button>);
            case "overlay": return (
                <OverlayTrigger key={i} overlay={b.overlay} placement={b.placement ?? "right"} rootClose={b.rootClose ?? true} trigger="click">
                    <button {...btnProps} className={"btn p-1"} data-tooltip={b.tooltip} onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}>{b.icon}</button>
                </OverlayTrigger>
            );
            case "link": return <Link key={i} to={b.to ?? ""} onClick={handleClick}>{b.icon}</Link>;
            default: return <Button {...btnProps} key={i}>{b.title}</Button>;
        }
    });
    const handleMouseEnter = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const tooltip = e.currentTarget.getAttribute("data-tooltip");
        if (!(tooltip?.length > 0)) {
            return;
        }
        let tooltipWidth = getTextWidth(tooltip, "10px Vazir");
        if (tooltipWidth > 300) {
            tooltipWidth = 300;
        }
        e.currentTarget.closest("table")
        let x = rect.left + rect.width / 2;
        const tblLeft = e.currentTarget.closest(".table-responsive").getBoundingClientRect().x;
        //برای اینکه tooltip از صفحه بیرون نرود
        if (x + tooltipWidth / 2 > window.innerWidth) {
            x = window.innerWidth - tooltipWidth / 2;
        }
        // if(Math.abs(x-tooltipWidth /2) < tblLeft)
        // {
        //     x = tblLeft;
        // }

        if (x - tooltipWidth / 2 < 0) {
            x = tooltipWidth / 2;
        }

        onShowTooltip({
            text: tooltip,
            x,
            y: rect.top
        });
    };
    const showTdTooltip = (item) => {
        let value = row[item.colName];
        if(typeof item.tooltip === "function")
        {
            item.hasTooltip = true;
            item.maxTextLength = 0;
            value = item.tooltip(row);
        }
        const e = item.e;
        if (!item.hasTooltip || !value || item.maxTextLength > (value.length??0)) {
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const tooltipWidth = e.currentTarget.closest("td").offsetWidth;
        if (!(tooltipWidth > 0)) {
            return;
        }
        let x = rect.left + rect.width / 2;
        //برای اینکه tooltip از صفحه بیرون نرود
        if (x + tooltipWidth / 2 > window.innerWidth) {
            x = window.innerWidth - tooltipWidth / 2;
        }

        if (x - tooltipWidth / 2 < 0) {
            x = tooltipWidth / 2;
        }

        onShowTooltip({
            text: value,
            x,
            y: rect.top + 5,
            width: tooltipWidth,
            showCopy: true
        });
    };
    const handleMouseLeave = () => {
        onShowTooltip(null);
    };
    return (
        <Fragment key={indx}>
            <tr className="resultTableRow" onClick={onRowClick} onDoubleClick={onRowDoubleClick} data-tooltip="">
                {rowNumber && <td style={{ width: "40px", backgroundColor: "#b6b6b6", borderRight: "1px solid #b3b3bb" }} key={-1}>{indx + 1}</td>}
                {visibleCols.map(renderCell)}
                {isWeb && buttons?.length > 0 && <td style={{ left: 0, background: "inherit" }} key={visibleCols?.length + 1}>{renderButtons()}</td>}
            </tr>
            {isMobile && (
                <tr style={{ display: displayRow === indx ? "table-row" : "none" }}>
                    <td colSpan={mobileCols.length + (rowNumber ? 1 : 0)} style={{ border: "1px solid #a7aab6", borderRadius: "2px" }}>
                        <div className="row">
                            {webCols.map((item, i) => (item.showCondition === undefined || item.showCondition(row)) && (
                                <Fragment key={i}>
                                    <div className="col-5 mt-2">{item.colTitle}</div>
                                    <div className="col-7 mt-2" style={{ direction: item.colType === "currency" ? "ltr" : "rtl", overflow: "auto", whiteSpace: "normal", textAlign: "justify" }}>
                                        {getCellContent(item)}
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                        <div className="row" style={{ padding: "5px 10px" }}>
                            <div className="col-md-4">{renderButtons()}</div>
                        </div>
                    </td>
                </tr>
            )}
        </Fragment>
    );
});

export default TableRow;







// import { Fragment, useRef, useState } from "react";
// import { Button, OverlayTrigger } from "react-bootstrap";
// import { Trash, EyeFill, Link45deg, Paperclip } from "react-bootstrap-icons";
// import { Link } from "react-router-dom";
// import {  isWebApp } from "../store/api/CommonFunctions";

// const TableRow = (props) => {
//     const { row, onclick, cols, indx, buttons, onDoubleClick, forceRowClickInMobile } = props;
//     const [dispalyDetail, setDisplay] = useState(false);
//     const buttonClicked = useRef(false);
//     let mobileCols = cols.filter(c => c.showInMobile && c.colName.toLowerCase() !== "rownumber");
//     let webCols = cols.filter(c => !c.showInMobile && c.colName.toLowerCase() !== "rownumber");
//     const rowNumber = cols.find(c => c.colName.toLowerCase() === "rownumber");
//     const isWeb = !isMobile;
//     const isMobile = isMobileApp();
//     const rowClick = (item) => {
//         if (item.target.tagName.toLowerCase() !== "td") {
//             return;
//         }
//         if (buttonClicked.current) {
//             buttonClicked.current = false;
//             return;
//         }
//         if (isMobile) {
//             setDisplay(!dispalyDetail);
//         }
//         if (isWeb || forceRowClickInMobile) {
//             if (onclick) {
//                 onclick(item);
//             }
//         }
//     }
//     function colValueChanged(e, fn) {
//         if (fn) {
//             fn({ e, ...row });
//         }
//     }
//     function onRowDoubleClick() {
//         if (onDoubleClick) {
//             onDoubleClick({ ...row, RowIndex: indx })
//         }
//     }
//     function getColData(row, colName, seperator) {
//         const datacols = colName.split("+");
//         let data = "";
//         for (let i = 0; i < datacols.length; i++) {
//             const rowData = row[datacols[i]];
//             if (rowData && rowData !== "") {
//                 data += rowData + (seperator ?? "-") + " ";
//             }
//         }
//         if (data.length > 1) {
//             data = data.substring(0, data.length - 2);
//         }
//         return data;
//     }
//     return (
//         //DataIndex: این فیلد برای وقتی استفاده می شود که خود ردیف یک فیلد بنام RowIndex دارد
//         <Fragment key={indx}>
//             <tr className="resultTableRow" onClick={(e) => rowClick({ ...row, DataIndex: row.RowIndex, RowIndex: indx, ctrlIsDown: e.ctrlKey, target: e.target })} onDoubleClick={onRowDoubleClick} style={{ background: indx % 2 === 0 ? "#e9e9e9" : "#FFF" }}>
//                 {rowNumber && <td style={{ width: "40px", backgroundColor: "#b6b6b6", borderRight: "1px solid #b3b3bb" }}>{indx + 1}</td>}
//                 {cols.filter(c => c.colName.toLowerCase() !== "rownumber").map((item, i) =>
//                     (item.showCondition === undefined || item.showCondition() === true) &&
//                     (isWeb || (isMobile && item.showInMobile)) &&
//                     <td key={i} style={item.style ? item.style : item.styleCondition ? item.styleCondition(row) : {}} className={item.hasTooltip ? "tooltipCol" : ""} colname={item.colName}>
//                         {item.colType === "currency" &&
//                             (!row[item.colName] ? 0 : (item.showNegative ? row[item.colName] : Math.abs(row[item.colName])).toLocaleString(navigator.language, { minimumFractionDigits: 0 }))}
//                         {item.colType === "boolean" && <input type="checkbox" className="tableRowColCheck form-check-input" checked={row[item.colName]} disabled={!(item.editable ?? false)} onChange={e => colValueChanged(e, item.onChange)} />}
//                         {item.colType === "number" && row[item.colName]}
//                         {item.colType === "string" && row[item.colName] && !item.colName.contains("+") &&
//                             (row[item.colName]?.substring(0, item.maxTextLength ? item.maxTextLength : row[item.colName].length) + (item.maxTextLength < row[item.colName].length ? "..." : ""))
//                         }
//                         {item.colType === "string" && item.colName.contains("+") &&
//                             getColData(row, item.colName, item.seprator)
//                             // (row[item.colName.split("+")[1]] ?
//                             //     (row[item.colName.split("+")[0] ?? ""] + (item.seprator ?? " - ") + row[item.colName.split("+")[1] ?? ""]) :
//                             //     (row[item.colName.split("+")[0] ?? ""]))
//                         }
//                         {item.colType === "color" && row[item.colName] &&
//                             <input type="color" readOnly value={row[item.colName]} disabled style={{ padding: 0, border: "none", width: "100%", minWidth: "20px", verticalAlign: "middle" }} />
//                         }
//                         {item.colType === "calc" && item.valueFunction(row)}
//                         {item.colType === "img" && <img src={"data:image/png;base64," + row[item.colName]} width={100} alt="بدون تصویر" onClick={item.onClick ? () => item.onClick(row) : null} />}
//                         {!item.colType && row[item.colName]}
//                         {item.hasTooltip && row[item.colName] && item.maxTextLength < row[item.colName].length && <span className="tooltipSpan">{row[item.colName]}</span>}
//                         {item.button && (!item.button.showCondition || item.button.showCondition(row)) &&
//                             <button className={(item.button.className??"")+" table-row-button"} onClick={item.button.onClick ? () => item.button.onClick(row) : null}>{item.button.icon}{item.button.caption}</button>
//                         }
//                     </td>)}
//                 {
//                     isWeb &&
//                     <Fragment>
//                         {
//                             buttons?.length > 0 &&
//                             <td style={{ position: "sticky", left: 0, background: "inherit" }}>
//                                 {buttons.map((b, i) => {
//                                     return (
//                                         (b.showCondition === undefined || b.showCondition(row) === true) &&
//                                         <Fragment key={i}>

//                                             {b.type === "delete" &&
//                                                 <Trash className="rowBtn" color="red" size={18} onClick={() => { buttonClicked.current = true; b.onclick(row) }} style={b.style} />
//                                             }
//                                             {b.type === "show" &&
//                                                 <EyeFill className="rowBtn" style={{ marginRight: "3px", ...b.style }} color="#9377d1" size={18} onClick={(e) => { buttonClicked.current = true; b.onclick({ ...row, e }) }} />
//                                             }
//                                             {b.type === "attach" &&
//                                                 <Paperclip className="rowBtn" style={{ marginRight: "3px", ...b.style }} color="blue" size={18} onClick={(e) => { buttonClicked.current = true; b.onclick({ ...row, e }) }} />
//                                             }
//                                             {b.type === "related" &&
//                                                 <Link45deg className="rowBtn" style={{ marginRight: "3px", ...b.style }} color="blue" size={18} onClick={(e) => { buttonClicked.current = true; b.onclick({ ...row, e }) }} />
//                                             }
//                                             {b.type === "common" &&
//                                                 <button className={"rowBtn btn" + (b.tooltip ? " tooltipBtn" : "")} style={{ padding: 0, marginRight: "3px" }} onClick={(e) => { buttonClicked.current = true; b.onclick({ ...row, e }) }}>
//                                                     {b.tooltip !== undefined && <span className="btnTooltip">{b.tooltip}</span>}
//                                                     {b.icon}
//                                                 </button>
//                                             }
//                                             {b.type === "overlay" &&
//                                                 <OverlayTrigger overlay={b.overlay} placement={b.placement ?? "right"} rootClose={b.rootClose === undefined ? true : b.rootClose} trigger="click">
//                                                     <button className={"rowBtn btn" + (b.tooltip ? " tooltipBtn" : "")} style={{ padding: 0, marginRight: "3px" }} onClick={(e) => { buttonClicked.current = true; if (b.onclick) { b.onclick({ ...row, e }); } }}>
//                                                         {b.tooltip !== undefined && <span className="btnTooltip">{b.tooltip}</span>}
//                                                         {b.icon}
//                                                     </button>
//                                                 </OverlayTrigger>
//                                             }
//                                             {b.type === "link" &&
//                                                 <Link onClick={(e) => { buttonClicked.current = true; b.onclick({ ...row, e }) }} to={b.to ?? ""}>{b.icon}</Link>
//                                             }
//                                             {!b.type &&
//                                                 <Button style={{ marginRight: "3px" }} onClick={(e) => { buttonClicked.current = true; b.onclick({ ...row, e }) }} className={"rowBtn " + (b.className ?? "")}>{b.title}</Button>
//                                             }
//                                         </ Fragment>)
//                                 })}
//                             </td>
//                         }
//                     </Fragment>
//                 }
//             </tr>
//             {
//                 isMobile &&
//                 <tr style={{ display: dispalyDetail ? "table-row" : "none" }}>
//                     <td colSpan={mobileCols?.length + (rowNumber ? 1 : 0)} style={{ border: "1px solid #a7aab6", borderRadius: "2px" }}>
//                         <div className='row'>
//                             {webCols.map((item, i) => {
//                                 return (
//                                     (item.showCondition === undefined || item.showCondition() === true) &&
//                                     <Fragment key={i}>
//                                         <div className="col-5 mt-2"> {item.colTitle}</div>
//                                         <div className="col-7  mt-2" style={{ direction: item.colType === "currency" ? "ltr" : "rtl", overflow: "auto" }} >
//                                             {item.colType === "currency" && row[item.colName]?.toLocaleString(navigator.language, { minimumFractionDigits: 0 })}
//                                             {item.colType === "boolean" && <input type="checkbox" readOnly checked={row[item.colName] ?? false} />}
//                                             {item.colType === "number" && row[item.colName]}
//                                             {item.colType === "color" && row[item.colName] &&
//                                                 <input type="color" readOnly value={row[item.colName]} disabled style={{ padding: 0, border: "none", width: "100%", minWidth: "20px", verticalAlign: "middle" }} />
//                                             }
//                                             {item.colType === "string" && row[item.colName] && !item.colName.contains("+") &&
//                                                 (row[item.colName]?.substring(0, item.maxTextLength ? item.maxTextLength : row[item.colName].length) + (item.maxTextLength < row[item.colName].length ? "..." : ""))
//                                             }
//                                             {item.colType === "string" && item.colName.contains("+") &&
//                                                 // (row[item.colName.split("+")[0] ?? ""] + " - " + row[item.colName.split("+")[1] ?? ""])
//                                                 getColData(row, item.colName)
//                                             }
//                                             {item.colType === "img" && <img src={"data:image/png;base64," + row[item.colName]} width={100} alt="بدون تصویر" onClick={item.onClick ? () => item.onClick(row) : null} />}
//                                             {!item.colType && row[item.colName]}
//                                         </div>
//                                     </Fragment>
//                                 )
//                             })}
//                         </div>
//                         <div className="row" style={{ padding: "5px", paddingRight: "10px" }}>
//                             <div className="col-md-4" >
//                                 {buttons?.length > 0 && buttons.map((b, i) => {
//                                     return ((b.showCondition === undefined || b.showCondition(row) === true) && <Button key={"btn" + i} variant={b.type === "delete" ? "danger" : "primary"} onClick={() => { b.onclick(row) }} className={b.className} style={b.style}>{b.title}</Button>)
//                                 }
//                                 )}
//                             </div>
//                         </div>
//                     </td>
//                 </tr>

//             }
//         </Fragment>
//     );
// }
// export default TableRow;
